import { EVENT_CONTENT, type ProgramItem } from "@tih/shared";

export type EventDetailsFormState = {
  doors: string;
  duration: string;
  venueName: string;
  venueAddress: string;
  venueCoords: string;
  venueMapsQuery: string;
  venueMapsUrl: string;
  venueEmbedUrl: string;
  useCustomProgram: boolean;
  program: ProgramItem[];
};

export const EMPTY_EVENT_DETAILS: EventDetailsFormState = {
  doors: "",
  duration: "",
  venueName: "",
  venueAddress: "",
  venueCoords: "",
  venueMapsQuery: "",
  venueMapsUrl: "",
  venueEmbedUrl: "",
  useCustomProgram: false,
  program: [],
};

/** Hydrate form from stored detailsJson overrides (empty = use site defaults). */
export function detailsFromJson(json: unknown): EventDetailsFormState {
  if (json == null || typeof json !== "object") return { ...EMPTY_EVENT_DETAILS };
  const d = json as Record<string, unknown>;
  const venue =
    d.venue != null && typeof d.venue === "object"
      ? (d.venue as Record<string, unknown>)
      : {};
  const program = Array.isArray(d.program)
    ? (d.program as ProgramItem[]).map((p) => ({
        time: String(p.time ?? ""),
        title: String(p.title ?? ""),
        note: String(p.note ?? ""),
      }))
    : [];
  return {
    doors: typeof d.doors === "string" ? d.doors : "",
    duration: typeof d.duration === "string" ? d.duration : "",
    venueName: typeof venue.name === "string" ? venue.name : "",
    venueAddress: typeof venue.address === "string" ? venue.address : "",
    venueCoords: typeof venue.coords === "string" ? venue.coords : "",
    venueMapsQuery: typeof venue.mapsQuery === "string" ? venue.mapsQuery : "",
    venueMapsUrl: typeof venue.mapsUrl === "string" ? venue.mapsUrl : "",
    venueEmbedUrl: typeof venue.embedUrl === "string" ? venue.embedUrl : "",
    useCustomProgram: program.length > 0,
    program,
  };
}

/** Build detailsJson patch from form; undefined = no overrides. */
export function detailsToJson(form: EventDetailsFormState): unknown | undefined {
  const patch: Record<string, unknown> = {};
  if (form.doors.trim()) patch.doors = form.doors.trim();
  if (form.duration.trim()) patch.duration = form.duration.trim();

  const venue: Record<string, string> = {};
  if (form.venueName.trim()) venue.name = form.venueName.trim();
  if (form.venueAddress.trim()) venue.address = form.venueAddress.trim();
  if (form.venueCoords.trim()) venue.coords = form.venueCoords.trim();
  if (form.venueMapsQuery.trim()) venue.mapsQuery = form.venueMapsQuery.trim();
  if (form.venueMapsUrl.trim()) venue.mapsUrl = form.venueMapsUrl.trim();
  if (form.venueEmbedUrl.trim()) venue.embedUrl = form.venueEmbedUrl.trim();
  if (Object.keys(venue).length) patch.venue = venue;

  if (form.useCustomProgram) {
    const program = form.program
      .map((p) => ({
        time: p.time.trim(),
        title: p.title.trim(),
        note: p.note.trim(),
      }))
      .filter((p) => p.time || p.title);
    if (program.length) patch.program = program;
  }

  return Object.keys(patch).length ? patch : undefined;
}

export function hasDetailsOverrides(form: EventDetailsFormState): boolean {
  return detailsToJson(form) !== undefined;
}

/** Default placeholders from bundled EVENT_CONTENT. */
export const EVENT_DETAILS_DEFAULTS = {
  doors: EVENT_CONTENT.doors,
  duration: EVENT_CONTENT.duration,
  venueName: EVENT_CONTENT.venue.name,
  venueAddress: EVENT_CONTENT.venue.address,
  venueCoords: EVENT_CONTENT.venue.coords,
};

export function defaultProgramCopy(): ProgramItem[] {
  return EVENT_CONTENT.program.map((p) => ({ ...p }));
}
